import { type FC, useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { AutoSizer, VirtualTable } from "@snapchat/snap-design-system";
import styled from "@emotion/styled";
import { SearchableCard } from "../../common/components/SearchableCard";
import { useTableFilterAndSort } from "../../common/hooks/useTableFilterAndSort";
import { NoResults } from "../../groups/components";
import { AppListItemModel } from "../models";
import { getColumns } from "./AppListColumns";
import { type RouteParams, AbsoluteRoute } from "../../../core/routes";

type ExtendedAppListItemModel = AppListItemModel & Record<string, unknown>;

type AppListProps = {
    isLoading?: boolean;
    apps: AppListItemModel[];
};

export const AppList: FC<AppListProps> = ({ isLoading, apps }) => {
    const navigate = useNavigate();
    const { orgId } = useParams<RouteParams[AbsoluteRoute.ORG_APPS]>();
    const [searchText, setSearchText] = useState("");
    const onSearch = useCallback((text: string) => setSearchText(text), [setSearchText]);
    const columns = useMemo(() => getColumns(), []);
    const loadMoreData = useCallback((): Promise<unknown> => new Promise(() => null), []);
    const isLastPage = true;

    const getSearchKey = useCallback((app: ExtendedAppListItemModel) => app.name ?? "", []);

    const { data: filteredData } = useTableFilterAndSort({
        tableData: apps as ExtendedAppListItemModel[],
        searchText,
        getSearchKey,
    });

    const onRowClick = useCallback(
        (_: unknown, rowIndex: number) => {
            const appId = filteredData[rowIndex]?.appId;
            if (orgId && appId) {
                navigate(
                    generatePath(AbsoluteRoute.ORG_APP, {
                        orgId,
                        appId,
                    }),
                );
            }
        },
        [filteredData, orgId, navigate],
    );

    return (
        <SearchableCardContainer
            padBody={false}
            isLoading={isLoading}
            searchPlaceholder="Search App Name"
            onSearch={onSearch}
            searchValue={searchText}
            tooltip="Apps are still loading."
        >
            <AutoSizer>
                {({ width, height }: { width: number; height: number }) => {
                    return (
                        <VirtualTable
                            isLoading={isLoading && apps.length === 0}
                            height={height}
                            width={width}
                            rowHeight={56}
                            columns={columns}
                            data={filteredData as ExtendedAppListItemModel[]}
                            onRowClick={onRowClick}
                            loadMore={loadMoreData}
                            hasMore={!isLastPage}
                            emptyMessage={<NoResults description="No apps found." />}
                        />
                    );
                }}
            </AutoSizer>
        </SearchableCardContainer>
    );
};

const SearchableCardContainer = styled(SearchableCard)`
    height: 80vh;

    .sds-vtable-row {
        cursor: pointer;
        width: 100%;
    }
`;
